export interface LocalAuthUser {
  id: string
  username: string
  email?: string
  firstName?: string
  lastName?: string
  roles: string[]
  authProvider: 'local'
  mustChangePassword?: boolean
}

export interface LocalAuthSession {
  accessToken: string
  expiresAt: number
  user: LocalAuthUser
}

import { markSessionEnded } from './sessionStore'

const STORAGE_KEY = 'rehapiano_local_auth_session'

export const getLocalSession = (): LocalAuthSession | null => {
  const raw = localStorage.getItem(STORAGE_KEY)
  if (!raw) {
    return null
  }

  try {
    const session = JSON.parse(raw) as LocalAuthSession
    if (!session?.accessToken || !session?.expiresAt || !session?.user) {
      localStorage.removeItem(STORAGE_KEY)
      return null
    }
    if (Date.now() >= session.expiresAt) {
      // The session really ran out (local JWTs are not silently renewed).
      // Remember it so the login page can explain instead of just appearing
      // after a reload — FE issue #24.
      localStorage.removeItem(STORAGE_KEY)
      markSessionEnded('expired')
      return null
    }
    return session
  } catch {
    localStorage.removeItem(STORAGE_KEY)
    return null
  }
}

export const saveLocalSession = (
  accessToken: string,
  expiresInSeconds: number,
  user: Omit<LocalAuthUser, 'authProvider'>,
) => {
  const session: LocalAuthSession = {
    accessToken,
    expiresAt: Date.now() + Math.max(1, expiresInSeconds) * 1000,
    user: {
      ...user,
      authProvider: 'local',
    },
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session))
}

export const clearLocalSession = () => {
  localStorage.removeItem(STORAGE_KEY)
}

export const getLocalAccessToken = (): string | null => {
  const session = getLocalSession()
  return session?.accessToken || null
}
